import { Badge } from '@/components/ui/badge';
import { getTagConfig, formatTagLabel } from '@/lib/customer-tags';
import { cn } from '@/lib/utils';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CustomerTagProps {
  tag: string;
  isManual?: boolean;
  showIcon?: boolean;
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const CustomerTag = ({ 
  tag, 
  isManual = true, 
  showIcon = true, 
  showTooltip = true,
  size = 'md',
  className 
}: CustomerTagProps) => {
  const config = getTagConfig(tag, isManual);
  const label = formatTagLabel(tag, isManual);
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tagRef = useRef<HTMLDivElement>(null);
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-1 h-5',
    md: 'text-xs px-2.5 py-1 h-6',
    lg: 'text-sm px-3 py-1.5 h-7'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };

  useEffect(() => {
    if (isHovered && tagRef.current) {
      const rect = tagRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2
      });
    }
  }, [isHovered]);

  if (!showTooltip || !config?.description) {
    return (
      <Badge
        variant="outline"
        className={cn(
          'inline-flex items-center gap-1.5 font-medium transition-all hover:scale-105',
          config?.color || (isManual ? 'bg-gray-100 text-gray-800 border-gray-200' : 'bg-slate-50 text-slate-700 border-slate-100'),
          sizeClasses[size],
          className
        )}
      >
        {showIcon && config?.icon && (
          <config.icon className={iconSizes[size]} />
        )}
        <span className="truncate">{label}</span>
        {!isManual && (
          <span className="ml-1 text-xs opacity-60">●</span>
        )}
      </Badge>
    );
  }

  return (
    <>
      <div 
        ref={tagRef}
        className="relative inline-block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Badge
          variant="outline"
          className={cn(
            'inline-flex items-center gap-1.5 font-medium transition-all hover:scale-105',
            config?.color || (isManual ? 'bg-gray-100 text-gray-800 border-gray-200' : 'bg-slate-50 text-slate-700 border-slate-100'),
            sizeClasses[size],
            className
          )}
        >
          {showIcon && config?.icon && (
            <config.icon className={iconSizes[size]} />
          )}
          <span className="truncate">{label}</span>
          {!isManual && (
            <span className="ml-1 text-xs opacity-60">●</span>
          )}
        </Badge>
      </div>
      {isHovered && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed max-w-xs whitespace-normal pointer-events-none"
          style={{ 
            zIndex: 2147483647,
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translateX(-50%)'
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="bg-popover text-popover-foreground rounded-md border px-3 py-1.5 text-sm shadow-lg animate-in fade-in-0 zoom-in-95 pointer-events-auto">
            <div className="space-y-1">
              <p className="font-semibold">{label}</p>
              <p className="text-xs opacity-80">{config.description}</p>
              <p className="text-xs opacity-60">
                {isManual ? 'Manuel Etiket' : 'Otomatik Etiket'}
              </p>
            </div>
            {/* Arrow pointing up */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-px">
              <div className="w-2 h-2 bg-popover border-l border-t rotate-45 transform"></div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

interface CustomerTagListProps {
  manualTags?: string[];
  autoTags?: string[];
  showIcons?: boolean;
  showTooltips?: boolean;
  size?: 'sm' | 'md' | 'lg';
  maxDisplay?: number;
  className?: string;
}

export const CustomerTagList = ({
  manualTags = [],
  autoTags = [],
  showIcons = true,
  showTooltips = true,
  size = 'md',
  maxDisplay,
  className
}: CustomerTagListProps) => {
  const allTags = [
    ...manualTags.map(tag => ({ tag, isManual: true })),
    ...autoTags.map(tag => ({ tag, isManual: false }))
  ];

  const displayTags = maxDisplay ? allTags.slice(0, maxDisplay) : allTags;
  const remainingCount = maxDisplay && allTags.length > maxDisplay ? allTags.length - maxDisplay : 0;

  if (allTags.length === 0) {
    return (
      <span className="text-sm text-muted-foreground italic">
        Etiket bulunmuyor
      </span>
    );
  }

  return (
    <div className={cn('flex flex-wrap gap-1.5 relative overflow-visible', className)}>
      {displayTags.map(({ tag, isManual }) => (
        <CustomerTag
          key={`${isManual ? 'manual' : 'auto'}-${tag}`}
          tag={tag}
          isManual={isManual}
          showIcon={showIcons}
          showTooltip={showTooltips}
          size={size}
        />
      ))}
      {remainingCount > 0 && (
        <Badge
          variant="outline"
          className={cn(
            'bg-muted text-muted-foreground border-muted',
            size === 'sm' && 'text-xs px-2 py-1 h-5',
            size === 'md' && 'text-xs px-2.5 py-1 h-6',
            size === 'lg' && 'text-sm px-3 py-1.5 h-7'
          )}
        >
          +{remainingCount}
        </Badge>
      )}
    </div>
  );
};
